<template>
  <div
    v-if="shouldRender"
    :class="wrapperClasses"
    @click="handleOverlayClick"
  >
    <div
      :class="cardClasses"
      @click.stop
      v-motion
      :initial="{ opacity: 0, scale: 0.8 }"
      :enter="{ opacity: 1, scale: 1 }"
      :leave="{ opacity: 0, scale: 0.8 }"
      :duration="300"
    >
      <!-- Header -->
      <div class="flex justify-between items-center mb-6 border-b border-gray-200 pb-4">
        <component
          :is="isPage ? 'h1' : 'h2'"
          class="text-3xl font-poppins font-bold text-dark-gray"
        >Terms of Service</component>
        <button
          v-if="!isPage"
          @click="closeModal"
          class="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors duration-200"
        >
          <X :size="20" class="text-dark-gray" />
        </button>
      </div>

      <!-- Content -->
      <div class="prose prose-gray max-w-none">
        <p class="text-sm text-gray-500 mb-6">Last updated: {{ lastUpdated }}</p>
        
        <section class="mb-8">
          <h3 class="text-xl font-semibold text-dark-gray mb-4">1. Acceptance of Terms</h3>
          <p class="text-dark-gray/80">
            By accessing and using Homvisit's services, you accept and agree to be bound by the terms 
            and provision of this agreement. If you do not agree to abide by the above, please do not 
            use this service.
          </p>
        </section>

        <section class="mb-8">
          <h3 class="text-xl font-semibold text-dark-gray mb-4">2. Services Provided</h3>
          <p class="text-dark-gray/80 mb-4">
            Homvisit provides healthcare services including but not limited to:
          </p>
          <ul class="list-disc pl-6 text-dark-gray/80 space-y-2">
            <li>Home blood collection services</li>
            <li>Corporate medical camps and health screenings</li>
            <li>Diagnostic consultations</li>
            <li>Health reports and analysis</li>
          </ul>
        </section>

        <section class="mb-8">
          <h3 class="text-xl font-semibold text-dark-gray mb-4">3. User Responsibilities</h3>
          <p class="text-dark-gray/80 mb-4">
            As a user of our services, you agree to:
          </p>
          <ul class="list-disc pl-6 text-dark-gray/80 space-y-2">
            <li>Provide accurate and complete information</li>
            <li>Follow pre-test instructions as provided</li>
            <li>Be present at the scheduled time for sample collection</li>
            <li>Pay for services as agreed upon</li>
            <li>Respect our healthcare professionals</li>
          </ul>
        </section>

        <section class="mb-8">
          <h3 class="text-xl font-semibold text-dark-gray mb-4">4. Appointment and Cancellation Policy</h3>
          <p class="text-dark-gray/80 mb-4">
            • Appointments must be scheduled at least 2 hours in advance<br>
            • Cancellations must be made at least 1 hour before the scheduled time<br>
            • No-show charges may apply for missed appointments<br>
            • We reserve the right to reschedule in case of emergencies
          </p>
        </section>

        <section class="mb-8">
          <h3 class="text-xl font-semibold text-dark-gray mb-4">5. Payment Terms</h3>
          <p class="text-dark-gray/80">
            Payment is due at the time of service unless other arrangements have been made. 
            We accept cash, digital payments, and corporate billing arrangements. 
            Prices are subject to change with prior notice.
          </p>
        </section>

        <section class="mb-8">
          <h3 class="text-xl font-semibold text-dark-gray mb-4">6. Limitation of Liability</h3>
          <p class="text-dark-gray/80">
            Homvisit shall not be liable for any indirect, incidental, special, consequential, 
            or punitive damages, including without limitation, loss of profits, data, use, 
            goodwill, or other intangible losses.
          </p>
        </section>

        <section class="mb-8">
          <h3 class="text-xl font-semibold text-dark-gray mb-4">7. Medical Disclaimer</h3>
          <p class="text-dark-gray/80">
            Our services are for diagnostic purposes only. Test results should be interpreted 
            by qualified healthcare professionals. We do not provide medical advice, diagnosis, 
            or treatment recommendations.
          </p>
        </section>

        <section class="mb-8">
          <h3 class="text-xl font-semibold text-dark-gray mb-4">8. Contact Information</h3>
          <p class="text-dark-gray/80">
            For questions regarding these Terms of Service, please contact us:
          </p>
          <div class="mt-4 p-4 bg-light-purple/10 rounded-lg">
            <p class="text-dark-gray font-medium">Email: sujithvijayan@homvisit.com</p>
            <p class="text-dark-gray font-medium">Phone: 75 9409 9409</p>
            <p class="text-dark-gray font-medium">Address: Homvisit, Trivandrum, Kerala</p>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue'
import { X } from 'lucide-vue-next'
import {
  breadcrumbSchema,
  graphSchema,
  organizationSchema,
  useSeo,
} from '../composables/useSeo'

export default {
  name: 'TermsOfService',
  components: {
    X
  },
  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    isPage: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close'],
  setup(props, { emit }) {
    const lastUpdated = computed(() => new Date().toLocaleDateString())
    const shouldRender = computed(() => props.isPage || props.isVisible)
    const wrapperClasses = computed(() =>
      props.isPage
        ? 'min-h-screen bg-gradient-to-b from-white to-light-purple/10 py-16 px-4 flex justify-center items-start'
        : 'fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4'
    )
    const cardClasses = computed(() =>
      props.isPage
        ? 'card-neumorphic max-w-4xl w-full shadow-xl'
        : 'card-neumorphic max-w-4xl w-full max-h-[90vh] overflow-y-auto'
    )

    if (props.isPage) {
      useSeo({
        title: 'Terms of Service | Homvisit Kerala Legal Terms Guide',
        description:
          'Terms governing Homvisit corporate health camps, onsite phlebotomy services, lab partnerships, and phlebotomist network participation in Kerala.',
        path: '/terms-of-service',
        schema: graphSchema([
          organizationSchema(),
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Terms of Service', path: '/terms-of-service' },
          ]),
        ]),
      })
    }

    const closeModal = () => {
      emit('close')
    }

    const handleOverlayClick = () => {
      if (!props.isPage) {
        closeModal()
      }
    }

    return {
      lastUpdated,
      shouldRender,
      wrapperClasses,
      cardClasses,
      closeModal,
      handleOverlayClick
    }
  }
}
</script>
